import { create } from "@bufbuild/protobuf";
import { type FieldMask } from "@bufbuild/protobuf/wkt";
import {
	AbacusService,
	ActivityFilterSchema,
	BulkImportContactsRequestSchema,
	ExportZipFileRequestSchema,
	GetPrimaryIdentifierCountsRequestSchema,
	GetRowCountRequestSchema,
	ListConversionRatesRequestSchema,
	ListMetadataKeysRequestSchema,
	StreamZipFileRequestSchema,
	type ActivityFilter,
	type AudienceSummary,
	type ChartRequest,
	type Contact,
	type ContactSchemaPropertyFilter,
	type ContactsFilter,
	type ContactsFilterOptions,
	type ContactsSummary,
	type ConversionFilter,
	type ConversionRateFilter,
	type ConversionRateSummary,
	type ConversionTypeFilter,
	type ConversionTypeSummary,
	type CreateContactSchemaResponse,
	type DashboardFilter,
	type ExperimentSummary,
	type ExperimentVariant,
	type ExportSource,
	type FormField,
	type GeoAdminLevel,
	type GetContactSchemaResponse,
	type GetConversionRateTotalsResponse,
	type GetRowCountResponse,
	type HideContactsResponse,
	type Interval,
	type ListActivityRequest,
	type ListContactsRequest,
	type ListConversionRatesResponse,
	type ListFormsResponse,
	type ListMetadataKeysResponse,
	type ListProductsResponse,
	type ListUserSegmentsRequest,
	type ListUserSegmentsResponse,
	type NamedCsvRequest,
	type PaginatedContactsFilterType,
	type Period,
	type RevenueSummary,
	type SchemaProperty,
	type TimeRange,
	type UpdateContactSchemaResponse,
	type VerifyContactsResponse,
	type Viewport,
} from "#gen/abacus/v2/abacus_pb.js";
import type { Dimension, Metric } from "#gen/abacus/v2/fields_pb.js";
import type { ConversionType, Event, User } from "#gen/bundles/v1/bundle_pb.js";
import { type Order, type PaginationArguments, type PaginationPageInfo } from "#gen/common/v1/pagination_pb.js";
import type { MappedValue } from "#gen/workflows/v1/workflows_pb.js";
import { createClient, type ClientOptions } from "#lib/api/api.js";
import { handleError } from "#lib/api/error.js";
import { downloadBlob } from "#lib/blob.js";
import { PUBLIC_URLS } from "#lib/url.js";
import type { CallOptions, Client } from "@connectrpc/connect";

export interface ContactList {
	contacts: Contact[];
	pageInfo?: PaginationPageInfo;
}

export interface EventList {
	events: Event[];
	pageInfo?: PaginationPageInfo;
}

export function toDeviceType(deviceType: string | undefined): string {
	switch (deviceType) {
		case "DEVICE_TYPE_DESKTOP":
			return "Desktop";
		case "DEVICE_TYPE_IOS":
			return "iOS";
		case "DEVICE_TYPE_ANDROID":
			return "Android";
		default:
			return "Unknown";
	}
}

export class AbacusClient {
	private client: Client<typeof AbacusService>;
	private includeSharedWorkspaces: boolean = false;
	private options: (() => ClientOptions) | undefined;

	constructor(options?: () => ClientOptions) {
		this.client = createClient(PUBLIC_URLS.abacus || "/", AbacusService, options);
		this.options = options!;
	}

	setIncludeSharedWorkspaces(include: boolean): void {
		this.includeSharedWorkspaces = include;
	}

	getIncludeSharedWorkspaces(): boolean {
		return this.includeSharedWorkspaces;
	}

	withIncludeSharedWorkspaces(include: boolean): AbacusClient {
		const clone = new AbacusClient(this.options);
		clone.setIncludeSharedWorkspaces(include);
		return clone;
	}

	async listContacts(orgId: string, workspaceId: string, interval?: Interval, timeRange?: TimeRange, timezone?: string, filter?: ContactsFilter, enrichMask?: FieldMask, workspacePropertyKeys?: string[], pagination?: PaginationArguments, options?: CallOptions): Promise<ContactList> {
		try {
			const res = await this.client.listContacts(
				{
					orgId,
					workspaceId,
					includeSharedWorkspaces: this.includeSharedWorkspaces,
					interval,
					timeRange,
					timezone,
					filter,
					enrichMask,
					workspacePropertyKeys,
					pagination,
				},
				options,
			);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getContact(orgId: string, workspaceId: string, primaryId: string, enrichMask?: FieldMask): Promise<Contact> {
		try {
			const res = await this.client.getContact({ orgId, workspaceId, includeSharedWorkspaces: this.includeSharedWorkspaces, primaryId, enrichMask });
			return res.contact!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getContacts(orgId: string, workspaceId: string, cookies: string[], options?: CallOptions): Promise<Record<string, User>> {
		try {
			const res = await this.client.getContactPropertiesFromSessions({ orgId, workspaceId, includeSharedWorkspaces: this.includeSharedWorkspaces, cookies }, options);
			return res.users;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listConversionsByConsumer(email: string, filter?: ConversionFilter, pagination?: PaginationArguments, options?: CallOptions): Promise<EventList> {
		try {
			const res = await this.client.listConversionsByConsumer(
				{
					email,
					filter,
					pagination,
				},
				options,
			);
			return { events: res.conversions, pageInfo: res.pageInfo };
		} catch (e) {
			throw handleError(e);
		}
	}

	async listActivity(orgId: string, workspaceId: string, interval?: Interval, timeRange?: TimeRange, timezone?: string, email?: string, filter?: ActivityFilter, pagination?: PaginationArguments, options?: CallOptions): Promise<EventList> {
		try {
			const res = await this.client.listActivity(
				{
					orgId,
					workspaceId,
					includeSharedWorkspaces: this.includeSharedWorkspaces,
					interval,
					timeRange,
					timezone,
					filter: filter ? create(ActivityFilterSchema, { ...filter, email: email ?? "" }) : create(ActivityFilterSchema, { email: email ?? "" }),
					pagination,
				},
				options,
			);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listForms(orgId: string, workspaceId: string, suiteId: string, interval?: Interval, timeRange?: TimeRange, timezone?: string, pagination?: PaginationArguments, options?: CallOptions): Promise<ListFormsResponse> {
		try {
			const res = await this.client.listForms(
				{
					orgId,
					workspaceId,
					suiteId,
					interval,
					timeRange,
					timezone,
					pagination,
				},
				options,
			);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listProducts(orgId: string, workspaceId: string, suiteId: string, interval?: Interval, timeRange?: TimeRange, timezone?: string, pagination?: PaginationArguments, options?: CallOptions): Promise<ListProductsResponse> {
		try {
			const res = await this.client.listProducts(
				{
					orgId,
					workspaceId,
					suiteId,
					interval,
					timeRange,
					timezone,
					pagination,
				},
				options,
			);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getConversionRateTotals(orgId: string, workspaceId: string, metrics: Metric[], interval: Interval, timeRange?: TimeRange, period?: Period, timezone?: string, filter?: ConversionRateFilter, dimensionGroupBy?: Dimension[]): Promise<GetConversionRateTotalsResponse> {
		try {
			const res = await this.client.getConversionRateTotals({
				orgId,
				workspaceId,
				metrics,
				includeSharedWorkspaces: this.includeSharedWorkspaces,
				interval,
				timeRange,
				period,
				timezone,
				filter,
				dimensionGroupBy,
			});
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getConversionRateSummary(orgId: string, workspaceId: string, metrics: Metric[], interval: Interval, timeRange?: TimeRange, period?: Period, timezone?: string, filter?: ConversionRateFilter, dimensionGroupBy?: Dimension[], maxSeries?: number, orderBy?: string, order?: Order, omitTotals?: boolean): Promise<ConversionRateSummary> {
		try {
			const res = await this.client.getConversionRateSummary({
				orgId,
				workspaceId,
				metrics,
				includeSharedWorkspaces: this.includeSharedWorkspaces,
				interval,
				timeRange,
				period,
				timezone,
				filter,
				dimensionGroupBy,
				maxSeries,
				orderBy,
				order,
				omitTotals,
			});
			return res.summary!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getExperimentSummary(orgId: string, workspaceId: string, interval: Interval, variants: ExperimentVariant[], timeRange?: TimeRange, period?: Period, timezone?: string, filter?: ConversionTypeFilter): Promise<ExperimentSummary> {
		try {
			const res = await this.client.getExperimentSummary({
				orgId,
				workspaceId,
				interval,
				timeRange,
				period,
				timezone,
				variants,
				filter,
			});
			return res.experimentSummary!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getRevenueSummary(orgId: string, workspaceId: string, interval: Interval, timeRange?: TimeRange, timezone?: string, filter?: ConversionTypeFilter): Promise<RevenueSummary> {
		try {
			const res = await this.client.getRevenueSummary({
				orgId,
				workspaceId,
				includeSharedWorkspaces: this.includeSharedWorkspaces,
				interval,
				timeRange,
				timezone,
				filter,
			});
			return res.summary!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getConversionTypeSummary(orgId: string, workspaceId: string, conversionType: ConversionType, interval: Interval, timeRange?: TimeRange, timezone?: string, filter?: ConversionTypeFilter): Promise<ConversionTypeSummary> {
		try {
			const res = await this.client.getConversionTypeSummary({
				orgId,
				workspaceId,
				includeSharedWorkspaces: this.includeSharedWorkspaces,
				interval,
				timeRange,
				timezone,
				conversionType,
				filter,
			});
			return res.summary!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listConversionRates(
		orgId: string,
		workspaceId: string | undefined,
		metrics: Metric[],
		dimensionGroupBy: Dimension[],
		interval: Interval,
		period?: Period,
		timeRange?: TimeRange,
		timezone?: string,
		filter?: ConversionRateFilter,
		pagination?: PaginationArguments,
		maxSeries?: number,
		variants?: ExperimentVariant[],
		omitTotals?: boolean,
		options?: CallOptions,
	): Promise<ListConversionRatesResponse> {
		try {
			const res = await this.client.listConversionRates(
				{
					orgId,
					workspaceId,
					metrics,
					includeSharedWorkspaces: this.includeSharedWorkspaces,
					dimensionGroupBy,
					interval,
					timeRange,
					timezone,
					filter,
					pagination,
					maxSeries,
					variants,
					period,
					omitTotals,
				},
				options,
			);
			return res!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getContactsSummary(orgId: string, workspaceId: string, interval: Interval, timeRange?: TimeRange, timezone?: string, filter?: ConversionTypeFilter, period?: Period): Promise<ContactsSummary> {
		try {
			const res = await this.client.getContactsSummary({
				orgId,
				workspaceId,
				includeSharedWorkspaces: this.includeSharedWorkspaces,
				interval,
				timeRange,
				timezone,
				filter,
				period,
			});
			return res.summary!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listFormFields(suiteId: string, formId?: string): Promise<FormField[]> {
		try {
			const res = await this.client.listFormFields({ suiteId, formId });
			return res.fields!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listMetadataKeys(orgId: string, workspaceId: string, suiteId?: string, key?: string, pagination?: PaginationArguments): Promise<string[]> {
		try {
			const res = await this.client.listMetadataKeys({ orgId, workspaceId, includeSharedWorkspaces: this.includeSharedWorkspaces, suiteId, key, pagination });
			return res.keys!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listMetadataKeysPaginated(orgId: string, workspaceId: string, suiteId?: string, key?: string, pagination?: PaginationArguments): Promise<ListMetadataKeysResponse> {
		try {
			const params = create(ListMetadataKeysRequestSchema, { orgId, workspaceId, includeSharedWorkspaces: this.includeSharedWorkspaces, suiteId, key, pagination });
			return await this.client.listMetadataKeys(params);
		} catch (e) {
			throw handleError(e);
		}
	}

	async getGeoSummary(orgId: string, workspaceId: string, viewport: Viewport, adminLevel: GeoAdminLevel, interval: Interval, timeRange?: TimeRange, timezone?: string, filter?: ConversionTypeFilter): Promise<string> {
		try {
			const res = await this.client.getGeoSummary({
				orgId,
				workspaceId,
				includeSharedWorkspaces: this.includeSharedWorkspaces,
				interval,
				timeRange,
				timezone,
				viewport,
				geoAdminLevel: adminLevel,
				filter,
			});
			const textDecoder = new TextDecoder();
			return textDecoder.decode(res.geojson);
		} catch (e) {
			throw handleError(e);
		}
	}

	async getAudienceSummary(orgId: string, workspaceId: string, interval: Interval, timeRange?: TimeRange, timezone?: string, filter?: ContactsFilter): Promise<AudienceSummary> {
		try {
			const res = await this.client.getAudienceSummary({
				orgId,
				workspaceId,
				includeSharedWorkspaces: this.includeSharedWorkspaces,
				interval,
				timeRange,
				timezone,
				contactsFilter: filter,
			});
			return res.summary!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getContactsFilterOptions(orgId: string, workspaceId: string, filterTypes: PaginatedContactsFilterType[], interval: Interval, timeRange?: TimeRange, timezone?: string, filter?: ContactsFilter, options?: CallOptions): Promise<ContactsFilterOptions[]> {
		try {
			const res = await this.client.getContactsFilterOptions(
				{
					orgId,
					workspaceId,
					includeSharedWorkspaces: this.includeSharedWorkspaces,
					interval,
					timeRange,
					timezone,
					paginatedContactsFilterTypes: filterTypes,
					filter,
				},
				options,
			);
			return res.contactsFilterOptions;
		} catch (e) {
			throw handleError(e);
		}
	}

	async streamCsvFile(req: { case: "listContactsRequest"; value: ListContactsRequest } | { case: "listActivityRequest"; value: ListActivityRequest }, formFields?: FormField[]): Promise<ReadableStream> {
		try {
			const ac = new AbortController();
			const stream = this.client.streamCsvFile(
				{
					request: req,
					formFields: formFields,
				},
				{ signal: ac.signal },
			);
			const newLine = new TextEncoder().encode("\n");
			return new ReadableStream({
				async start(controller) {
					for await (const chunk of stream) {
						const sub = chunk.row.subarray(1, chunk.row.length - 1);
						const row = new Uint8Array(sub.length + newLine.length);
						row.set(sub, 0);
						row.set(newLine, sub.length);
						controller.enqueue(row);
					}
					controller.close();
				},
				cancel() {
					ac.abort();
				},
			});
		} catch (e) {
			throw handleError(e);
		}
	}

	async streamPdfFile(chartRequests?: ChartRequest[], options?: CallOptions, dashboardId?: string, dashboardFilter?: DashboardFilter): Promise<string> {
		try {
			const stream = await this.client.streamPdfFile(
				{
					chartRequests: this.chartRequestsWithSharedWorkspaces(chartRequests),
					dashboardId,
					dashboardFilter,
				},
				options,
			);
			return stream.s3Url;
		} catch (e) {
			throw handleError(e);
		}
	}

	async streamZipFile(namedCsvRequests?: NamedCsvRequest[], chartRequests?: ChartRequest[], options?: CallOptions, dashboardId?: string, dashboardFilter?: DashboardFilter): Promise<string> {
		try {
			const link = await this.client.streamZipFile(
				create(StreamZipFileRequestSchema, {
					chartRequests: this.chartRequestsWithSharedWorkspaces(chartRequests),
					namedCsvRequests: this.namedCsvRequestsWithSharedWorkspaces(namedCsvRequests),
					dashboardId,
					dashboardFilter,
				}),
				options,
			);
			return link.s3Url;
		} catch (e) {
			throw handleError(e);
		}
	}

	async exportZipFile(source?: ExportSource, emailRecipients?: string[], chartRequests?: ChartRequest[], dashboardId?: string, dashboardFilter?: DashboardFilter): Promise<string> {
		try {
			const res = await this.client.exportZipFile(
				create(ExportZipFileRequestSchema, {
					chartRequests: this.chartRequestsWithSharedWorkspaces(chartRequests),
					dashboardId,
					dashboardFilter,
					emailRecipients,
					source,
				}),
			);
			return res.url;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getExportDownloadUrl(orgId: string, workspaceId: string | undefined, exportId: string, options?: CallOptions): Promise<string> {
		try {
			const res = await this.client.getExportDownloadUrl({ orgId, workspaceId, exportId }, options);
			return res.url;
		} catch (e) {
			throw handleError(e);
		}
	}

	async downloadZip(s3url: string, filename: string = "analytics"): Promise<void> {
		return this.options?.().fetch!(s3url, {
			method: "GET",
			mode: "cors",
			headers: {
				"Content-Type": "application/zip",
			},
		})
			.then(response => response.blob())
			.then(blob => {
				return new Promise((resolve, reject): void => {
					const reader = new FileReader();
					reader.onload = function () {
						if (typeof reader.result === "string") {
							downloadBlob(`${filename}.zip`, reader.result);
							resolve();
						}
						reject();
					};
					reader.onerror = reject;
					reader.readAsDataURL(blob);
				});
			});
	}

	async downloadPDF(s3url: string, filename: string = "analytics"): Promise<void> {
		return this.options?.().fetch!(s3url, {
			method: "GET",
			mode: "cors",
			headers: {
				"Content-Type": "application/pdf",
			},
		})
			.then(response => response.blob())
			.then(blob => {
				return new Promise((resolve, reject): void => {
					const reader = new FileReader();
					reader.onload = function () {
						if (typeof reader.result === "string") {
							downloadBlob(`${filename}.pdf`, reader.result);
							resolve();
						}
						reject();
					};
					reader.onerror = reject;
					reader.readAsDataURL(blob);
				});
			});
	}

	async listUserSegments(req: ListUserSegmentsRequest): Promise<ListUserSegmentsResponse> {
		try {
			const res = await this.client.listUserSegments(req);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async createContactSchema(orgId: string, workspaceId: string, properties: SchemaProperty[]): Promise<CreateContactSchemaResponse> {
		try {
			const res = await this.client.createContactSchema({
				orgId,
				workspaceId,
				properties,
			});
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async updateContactSchema(orgId: string, workspaceId: string, properties: SchemaProperty[], recipients?: string[]): Promise<UpdateContactSchemaResponse> {
		try {
			const res = await this.client.updateContactSchema({
				schema: {
					orgId,
					workspaceId,
					properties,
				},
				recipients,
			});
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getContactSchema(orgId: string, workspaceId: string, filter?: ContactSchemaPropertyFilter, paginate?: PaginationArguments, options?: CallOptions): Promise<GetContactSchemaResponse> {
		try {
			const res = await this.client.getContactSchema(
				{
					orgId,
					workspaceId,
					includeSharedWorkspaces: this.includeSharedWorkspaces,
					filter,
					pagination: paginate,
				},
				options,
			);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getListConversionRatesRowCount(orgId: string, workspaceId: string | undefined, dimensionGroupBy: Dimension[], interval: Interval, timeRange?: TimeRange, timezone?: string, filter?: ConversionRateFilter, period?: Period): Promise<GetRowCountResponse> {
		try {
			const params = create(GetRowCountRequestSchema, {
				request: {
					case: "listConversionRatesRequest",
					value: create(ListConversionRatesRequestSchema, {
						orgId,
						workspaceId,
						includeSharedWorkspaces: this.includeSharedWorkspaces,
						dimensionGroupBy,
						interval,
						timeRange,
						timezone,
						filter,
						period,
					}),
				},
			});
			const res = await this.client.getRowCount(params);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getPrimaryIdentifierCounts(orgId: string, workspaceId: string, properties: SchemaProperty[]) {
		try {
			const params = create(GetPrimaryIdentifierCountsRequestSchema, {
				orgId,
				workspaceId,
				properties,
			});
			const res = await this.client.getPrimaryIdentifierCounts(params);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async bulkImportContacts(orgId: string, workspaceId: string, fileUrl: string, mapping: { [key: string]: MappedValue }, hasHeader: boolean, header: string[], createAudience: boolean, audienceName?: string): Promise<void> {
		try {
			const params = create(BulkImportContactsRequestSchema, {
				orgId,
				workspaceId,
				fileUrl,
				mapping,
				hasHeader,
				header: hasHeader ? header : undefined,
				createAudience,
				audienceName,
			});
			await this.client.bulkImportContacts(params);
		} catch (e) {
			throw handleError(e);
		}
	}

	async hideContact(orgId: string, workspaceId: string, contact: Contact): Promise<void> {
		try {
			await this.client.hideContacts({ orgId, workspaceId, contacts: [contact] });
		} catch (e) {
			throw handleError(e);
		}
	}

	async hideContacts(orgId: string, workspaceId: string, listContactsRequest: ListContactsRequest): Promise<HideContactsResponse> {
		try {
			return await this.client.hideContacts({ orgId, workspaceId, listContactsRequest });
		} catch (e) {
			throw handleError(e);
		}
	}

	async verifyContact(orgId: string, workspaceId: string, contact: Contact): Promise<void> {
		try {
			await this.client.verifyContacts({ orgId, workspaceId, contacts: [contact] });
		} catch (e) {
			throw handleError(e);
		}
	}

	async verifyContacts(orgId: string, workspaceId: string, listContactsRequest: ListContactsRequest): Promise<VerifyContactsResponse> {
		try {
			return await this.client.verifyContacts({ orgId, workspaceId, listContactsRequest });
		} catch (e) {
			throw handleError(e);
		}
	}

	private chartRequestsWithSharedWorkspaces(chartRequests?: ChartRequest[]): ChartRequest[] | undefined {
		chartRequests?.forEach(cr => {
			if (cr.request.value) {
				cr.request.value.includeSharedWorkspaces = this.includeSharedWorkspaces;
			}
		});
		return chartRequests;
	}

	private namedCsvRequestsWithSharedWorkspaces(namedCsvRequests?: NamedCsvRequest[]): NamedCsvRequest[] | undefined {
		namedCsvRequests?.forEach(cr => {
			if (cr.request.value) {
				cr.request.value.includeSharedWorkspaces = this.includeSharedWorkspaces;
			}
		});
		return namedCsvRequests;
	}
}
